/**
 * Hybrid Image Component
 * Owns the load/error state + lifecycle in ONE place and switches only the
 * rendered leaf: the styled-components element when styled-system props are
 * present, otherwise the lighter Tailwind <img>.
 */

import * as React from "react";
import ImageContainer from "./styles"; // Styled-components version
import { ImageTailwind } from "./ImageTailwind"; // Tailwind version
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import type { TypeImageProps } from "./ImageTypes";

type TypeState = {
  didError: boolean;
  didLoad: boolean;
  imageUrl: string;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export default class ImageHybrid extends React.Component<
  TypeImageProps,
  TypeState
> {
  static defaultProps = {
    title: "",
    onError: noop,
    onClick: noop,
    onLoad: noop,
  };

  override state = {
    didError: false,
    didLoad: false,
    imageUrl: this.props.src,
  };

  imageRef?: HTMLImageElement | null;

  override componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    if (this.imageRef) {
      this.setState({
        didLoad: this.imageRef.complete,
      });
    }
  }

  override componentDidUpdate(prevProps: TypeImageProps) {
    if (this.props.src !== prevProps.src) {
      this.setState({
        didError: false,
        didLoad: false,
        imageUrl: this.props.src,
      });
    }
  }

  onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!this.state.didError && this.props.defaultImage) {
      this.setState({
        didError: true,
        imageUrl: this.props.defaultImage,
      });
    }

    this.props.onError(e);
  };

  onLoad = () => {
    this.setState({
      didLoad: true,
    });
    this.props.onLoad();
  };

  onClick = () => {
    this.props.onClick();
  };

  override render() {
    // defaultImage is consumed by onError (this.props.defaultImage) and must be
    // pulled out of rest so it never reaches the DOM <img> on the Tailwind path
    // (the styled-components leaf filtered it implicitly; a plain <img> does not).
    const {
      alt,
      title,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onClick,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onError,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onLoad,
      src,
      qa,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultImage,
      ...rest
    } = this.props;
    const isLoading = !this.state.didLoad;
    const refCb = (img: HTMLImageElement | null) => (this.imageRef = img);
    const shared = {
      onClick: this.onClick,
      src: this.state.imageUrl,
      onError: (e: React.SyntheticEvent<HTMLImageElement, Event>) =>
        this.onError(e),
      onLoad: this.onLoad,
      alt,
      title,
      "data-qa-image": title ? title : alt ? alt : "",
      "data-qa-image-src": src || "",
      ...(qa as any),
      ...rest,
    };

    if (hasStyledProps(rest)) {
      // Legacy styled-components leaf — styles.ts unchanged.
      return <ImageContainer ref={refCb} isLoading={isLoading} {...shared} />;
    }

    // Tailwind leaf (preferred - lighter, no styled-components runtime).
    return <ImageTailwind ref={refCb} isLoading={isLoading} {...shared} />;
  }
}
